/**
 * Guest cancellation deadline.
 * The supplier timestamp is not rewritten. The shown instant is 24 hours earlier
 * so the agent deadline is never later than the supplier deadline.
 * Naive supplier clocks are read as Asia/Jerusalem. A value that cannot be parsed
 * does not produce a free-cancellation promise.
 */

export const HUBWAYZ_CXL_LEAD_MS = 24 * 60 * 60 * 1000;
const IL_TZ = "Asia/Jerusalem";

function jerusalemOffsetMs(utcDate) {
  const tz = new Intl.DateTimeFormat("en-US", {
    timeZone: IL_TZ,
    timeZoneName: "shortOffset",
  })
    .formatToParts(utcDate)
    .find((part) => part.type === "timeZoneName")?.value;
  const match = String(tz || "").match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 2 * 60 * 60 * 1000;
  const sign = match[1] === "-" ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3] || 0)) * 60 * 1000;
}

function wallInJerusalemToUtc(year, month, day, hour, minute, second) {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let offset = jerusalemOffsetMs(new Date(asUtc));
  let utc = asUtc - offset;
  offset = jerusalemOffsetMs(new Date(utc));
  return new Date(asUtc - offset);
}

/**
 * @returns {{ instant: Date, hasTime: boolean, supplierRaw: string } | null}
 */
export function parseSupplierCancellationInstant(raw) {
  const supplierRaw = String(raw ?? "").trim();
  if (!supplierRaw) return null;
  const match = supplierRaw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?(Z|[+-]\d{2}:?\d{2})?$/
  );
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const hasTime = match[4] != null;
  const hasZone = Boolean(match[7]);
  let instant;
  if (hasZone) {
    const iso = supplierRaw.includes(" ")
      ? supplierRaw.replace(" ", "T")
      : supplierRaw;
    instant = new Date(iso);
  } else if (hasTime) {
    instant = wallInJerusalemToUtc(
      year,
      month,
      day,
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0)
    );
  } else {
    instant = wallInJerusalemToUtc(year, month, day, 23, 59, 59);
  }
  if (Number.isNaN(instant.getTime())) return null;
  return { instant, hasTime, supplierRaw };
}

export function internalCancellationInstant(supplierInstant) {
  return new Date(supplierInstant.getTime() - HUBWAYZ_CXL_LEAD_MS);
}

function formatJerusalem(date, withTime) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IL_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: withTime ? "2-digit" : undefined,
    minute: withTime ? "2-digit" : undefined,
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  const datePart = `${get("day")}/${get("month")}/${get("year")}`;
  if (!withTime) return datePart;
  return `${datePart} ${get("hour")}:${get("minute")}`;
}

/**
 * Hebrew free-cancellation line for the supplier deadline saved at booking.
 * This is not the earlier automatic cancellation of an unpaid hold.
 * Returns null when the supplier deadline cannot be read.
 */
export function formatSupplierFreeCancellationLabel(raw) {
  const parsed = parseSupplierCancellationInstant(raw);
  if (!parsed) return null;
  const text = formatJerusalem(parsed.instant, parsed.hasTime);
  return {
    instant: parsed.instant,
    text,
    label: `ביטול חינם עד ${text}`,
  };
}

/**
 * Hebrew free-cancellation line for the internal deadline.
 * Returns null when the supplier deadline cannot be read.
 */
export function formatInternalFreeCancellationLabel(raw) {
  const parsed = parseSupplierCancellationInstant(raw);
  if (!parsed) return null;
  const internal = internalCancellationInstant(parsed.instant);
  const withTime = parsed.hasTime;
  return `ביטול ללא עלות עד ${formatJerusalem(internal, withTime)}`;
}
